import EncryptedStorage from 'react-native-encrypted-storage';
import { Platform } from 'react-native';

/**
 * Кого человек уже поздравил или скрыл в этом году.
 *
 * <p>Локально, а не на сервере, и это осознанно. Сервер знает, что напоминание отправлено, но
 * не знает, что с ним сделали: поздравить можно и голосом, и в другом мессенджере, а карточка
 * должна уйти. Заводить ради этого серверное состояние значило бы синхронизировать между
 * устройствами то, что человек и так сделает на каждом по разу.
 *
 * <p>Ключ включает год: то же самое напоминание должно вернуться через двенадцать месяцев.
 * Отметки прошлых лет вычищаются при каждом чтении — иначе список рос бы вечно.
 *
 * <p>Ошибки чтения и записи глотаются: скрытая карточка — удобство, и уронить из-за него
 * список чатов нельзя.
 */
const KEY = 'vr_birthday_dismissed_v1';

function isWeb(): boolean {
  return Platform.OS === 'web';
}

/** Отметка — `userId:год`: тот же человек в следующем году считается непоздравленным. */
function markOf(userId: string, year: number): string {
  return `${userId}:${year}`;
}

async function readRaw(): Promise<string[]> {
  try {
    const raw = isWeb() ? globalThis.localStorage?.getItem(KEY) ?? null : await EncryptedStorage.getItem(KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

async function writeRaw(marks: string[]): Promise<void> {
  try {
    const value = JSON.stringify(marks);
    if (isWeb()) {
      globalThis.localStorage?.setItem(KEY, value);
      return;
    }
    await EncryptedStorage.setItem(KEY, value);
  } catch {
    /* ignore storage write errors */
  }
}

/** Кого скрывать сегодня. Заодно выбрасывает отметки прошлых лет. */
export async function readDismissedBirthdays(year: number): Promise<Set<string>> {
  const marks = await readRaw();
  const suffix = `:${year}`;
  const current = marks.filter((m) => m.endsWith(suffix));
  if (current.length !== marks.length) {
    void writeRaw(current);
  }
  return new Set(current.map((m) => m.slice(0, -suffix.length)));
}

export async function dismissBirthday(userId: string, year: number): Promise<void> {
  const marks = await readRaw();
  const mark = markOf(userId, year);
  if (marks.includes(mark)) return;
  await writeRaw([...marks, mark]);
}
